<template lang="pug">
  content-box(color='blue')
    template(slot='header') {{ title }}
    slot(name='before')
    div(:class='$style.container')
      div(:class='formClasses')
        slot(name='form')
      div(v-if='showMap' :class='$style["coverage-map"]')
        app-link(:to='countyDetailUrl' newTab :class='$style.maplink')
          county-coverage-map(:class='$style.map' :appCode='appCode.toUpperCase()')
          div(:class='$style.maphint')
            slot(name='map-hint') click here for county coverage details
      div(:class='$style.footer')
        slot(name='footer')
          p(v-if='showCounts && count') Providing over
            b  {{count.recordCount}} {{count.label}}
            span  {{count.countyTxt}}
          slot(v-if='$loggedIn' name='subscriber-user-footer')
            app-link(:to='advancedSearchUrl') Advanced Search
            app-link(:to='countyDetailUrl' :class='$style.coverage' newTab) Coverage Map
          slot(v-else name='anonymous-user-footer')
            |  #[app-link(to='/common/NewSubscription') Subscribe]
            |  or
            |  #[app-link(to='/common/Login' :params='loginLinkParams') Login]
            |  to access details and additional features.
            app-link(:to='countyDetailUrl' :class='$style.coverage' newTab) Coverage Map
</template>

<script>
import ContentBox from '@/components/content-box'
import CountyCoverageMap from '@/components/maps/county-coverage-map'
import AppLink from '@/components/app-link'
import l10n from '@/assets/recorder/l10n.js'

/**
 * Search Widget Component
 *
 */
export default {
  name: 'search-widget',
  components: {
    ContentBox,
    CountyCoverageMap,
    AppLink
  },
  props: {
    title: {
      type: String,
      required: true
    },
    appCode: {
      type: String,
      required: true
    },
    countyDetailUrl: {
      type: String,
      required: true
    },
    advancedSearchUrl: {
      type: String,
      required: true
    },
    showCounts: {
      type: Boolean,
      required: false,
      default: false
    },
    showMap: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  mounted () {
    this.$store.dispatch('app-props/fetch', this)
  },
  methods: {
    l10n (key) {
      return l10n(this)(key)
    }
  },
  computed: {
    loginLinkParams () {
      return {
        done: this.$store.getters.contextPath + this.$route.path
      }
    },
    formClasses () {
      if (this.showMap) {
        return [this.$style.form]
      }
      return [this.$style.form_no_map]
    },
    count () {
      if (this.appCode === 'COURT') {
        let courtCount = this.$store.getters['app-props/courtCount']
        let courtCountyCount = this.$store.getters['app-props/courtCountyCount']
        return {
          recordCount: this.$store.getters['app-props/courtCaseCount'],
          label: 'cases',
          countyTxt: `from ${courtCount} courts in ${courtCountyCount} ${this.l10n('terms.state')} counties.`
        }
      } else if (this.appCode === 'JUDGMENT') {
        let judgmentCourtCount = this.$store.getters['app-props/judgmentCourtCount']
        let judgmentCountyCount = this.$store.getters['app-props/judgmentCountyCount']
        return {
          recordCount: this.$store.getters['app-props/judgmentCount'],
          label: 'judgments',
          countyTxt: `from ${judgmentCourtCount} courts in ${judgmentCountyCount} ${this.l10n('terms.state')} counties.`
        }
      } else if (this.appCode === 'RECORDER') {
        let recorderCountyCount = this.$store.getters['app-props/recorderCountyCount']
        return {
          recordCount: this.$store.getters['app-props/documentCount'],
          label: 'recorded documents',
          countyTxt: `from ${recorderCountyCount} ${this.l10n('terms.state')} counties.`
        }
      } else if (this.appCode === 'REGISTER') {
        let recorderCountyCount = this.$store.getters['app-props/recorderCountyCount']
        return {
          recordCount: this.$store.getters['app-props/documentCount'],
          label: 'recorded documents',
          countyTxt: `from ${recorderCountyCount} ${this.l10n('terms.state')} counties.`
        }
      } else if (this.appCode === 'TW') {
        let taxWarrantCountyCount = this.$store.getters['app-props/taxWarrantCountyCount']
        return {
          recordCount: this.$store.getters['app-props/taxWarrantCount'],
          label: 'tax warrants',
          countyTxt: `from ${taxWarrantCountyCount} ${this.l10n('terms.state')} counties.`
        }
      } else {
        return {} // TODO
      }
    }
  }
}
</script>

<style lang="scss" module>
@import 'src/assets/styles/grid';

.container {
  @include display-grid();
  @include grid-template-columns(auto 10rem);
  @include grid-template-rows(auto auto);
  padding: 0.5rem 0;
}

.form {
  @include grid-row(1);
  @include grid-column(1);
}

.form_no_map {
  @include grid-row(1);
  @include grid-column(1, 2);
}

.coverage-map {
  @include grid-row(1);
  @include grid-column(2);
  margin-left: 1rem;
}

.footer {
  @include grid-row(2);
  @include grid-column(1, 2);
}

.map {
  max-height: 15rem;
}

.coverage {
  display: none;
}

.maplink {
  display: block;
}

.maphint {
  font-size: small;
  text-align: center;
}

.form > form, .form_no_map > form {
  width: 100%;
  display: block;

  :global(.errors) {
    color: red;
    display: block;
    caption-side: top;
    margin-bottom: 0.5rem;
  }

  :global(.input-group) {
    display: block;

    &:not(:first-child) {
      margin-top: 1rem;
    }

    :global(.label) {
      display: block;
      white-space: nowrap;
      font-weight: bold;
      text-transform: capitalize;
      padding-right: 0.5rem;
    }

    :global(.inputs) {
      display: block;
      padding-top: 5px;
    }
  }
}

@media screen and (max-width: 500px) {
  .map, .maphint {
    display: none;
  }
  .form {
    @include grid-row(1);
    @include grid-column(1, 2);
  }
  .coverage {
    display: inline-block;
    float: right;
  }
}
</style>
